import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import type { LiveState } from '@models/api';
import { RESULTS_PAGE_SIZE } from '@lib/livePolls/resultsPaging';
import { cn } from '@lib/utils';
import { LIVE_POLL_COPY } from '../livePollCopy';
import { StageHitArea } from './BigScreenParts';

/** What the → key and the forward button do from this state: show the next question, or the results. */
export const forwardMove = (state: LiveState): 'next' | 'end' | null => {
  if (state.removed || state.live_state !== 'live') return null;
  const last = state.questions_count - 1;
  return state.current_position !== null && state.current_position >= last ? 'end' : 'next';
};

/** Back is offered from question 2 on (Back on question 1 is refused, CONTRACT § 10 N16). */
export const canGoBack = (state: LiveState): boolean =>
  !state.removed &&
  state.live_state === 'live' &&
  state.current_position !== null &&
  state.current_position > 0;

/** The bar shows while the poll runs: from Start (B1b, "Get ready") to the results. */
export const hasAuthorBar = (state: LiveState): boolean => forwardMove(state) !== null;

/** The results' controls: only with more than one page to pause or skip (B3b, B3c). */
const hasPagingBar = (state: LiveState): boolean =>
  !state.removed &&
  state.live_state === 'ended' &&
  (state.results?.length ?? 0) > RESULTS_PAGE_SIZE;

export interface BigScreenAuthorBarProps {
  state: LiveState;
  /** Shown after a mouse move or key press, hidden 3 s after the last one. */
  shown: boolean;
  /** A move is in flight: every button holds. */
  busy: boolean;
  onBack: () => void;
  onForward: () => void;
  onEnd: () => void;
}

// AIDEV-NOTE: quiet on purpose (founder, 2026-11-03, walking TECH-867): "barely visible to somebody in the
// audience". No fill colour, no red, no shadow, no text beyond the labels; the laptop is usually the room's screen.
// The visual size shrinks, the 44px hit area stays (`StageHitArea`; off the stage its scale is 1, so 44px on screen).
const BUTTON =
  'relative inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-black/5 px-2.5 font-fira text-[13px] font-medium leading-4 text-black/60 transition-colors hover:bg-black/10 hover:text-black/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink aria-disabled:cursor-progress';

/**
 * The bottom right corner of the screen, drawn in the viewport's pixels, not on the scaled stage. Clear of every
 * layout there: the QR tiles and the count end higher, and the join panel's scan line ends above it (only a
 * three-line Space name at exactly 1280×720 reaches this high). Hidden, it is inert and out of the accessibility
 * tree.
 */
function QuietBar({
  shown,
  testId,
  children,
}: {
  shown: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'fixed bottom-3 right-3 z-20 flex items-center gap-1 transition-opacity duration-200 motion-reduce:transition-none',
        shown ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      aria-hidden={!shown}
      inert={!shown}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/**
 * B2b: the author's controls on their own signed-in screen: Back, "Next question" or "Show results" on the last
 * question, and "End poll". They appear on mouse move or a key press; ← and → work too.
 */
export function BigScreenAuthorBar({
  state,
  shown,
  busy,
  onBack,
  onForward,
  onEnd,
}: BigScreenAuthorBarProps) {
  const forward = forwardMove(state);
  if (forward === null) return null;
  const hold = (action: () => void) => () => {
    if (!busy) action();
  };
  return (
    <QuietBar shown={shown} testId="big-screen-author-bar">
      {canGoBack(state) ? (
        <button type="button" onClick={hold(onBack)} aria-disabled={busy} className={BUTTON}>
          <StageHitArea />
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
          {LIVE_POLL_COPY.back}
        </button>
      ) : null}
      <button type="button" onClick={hold(onForward)} aria-disabled={busy} className={BUTTON}>
        <StageHitArea />
        {forward === 'end' ? LIVE_POLL_COPY.showResults : LIVE_POLL_COPY.nextQuestion}
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
      </button>
      <button type="button" onClick={hold(onEnd)} aria-disabled={busy} className={BUTTON}>
        <StageHitArea />
        {LIVE_POLL_COPY.endPoll}
      </button>
    </QuietBar>
  );
}

export interface BigScreenPagingBarProps {
  state: LiveState;
  /** As the author bar: shown after a mouse move or key press, hidden 3 s after the last one. */
  shown: boolean;
  /** A paging call is in flight: the buttons hold. */
  busy: boolean;
  onPause: () => void;
  onResume: () => void;
  onNext: () => void;
}

/**
 * B3c: the author's paging controls on the results, Pause or Resume and Next page (they call
 * `POST /live/{code}/pages`, so every screen follows). Same corner and same look as the author bar.
 */
export function BigScreenPagingBar({
  state,
  shown,
  busy,
  onPause,
  onResume,
  onNext,
}: BigScreenPagingBarProps) {
  if (!hasPagingBar(state)) return null;
  const paused = state.results_paging?.paused === true;
  const hold = (action: () => void) => () => {
    if (!busy) action();
  };
  return (
    <QuietBar shown={shown} testId="big-screen-paging-bar">
      <button
        type="button"
        onClick={hold(paused ? onResume : onPause)}
        aria-disabled={busy}
        className={BUTTON}
      >
        <StageHitArea />
        {paused ? (
          <Play className="h-3 w-3 fill-current" strokeWidth={0} aria-hidden="true" />
        ) : (
          <Pause className="h-3 w-3 fill-current" strokeWidth={0} aria-hidden="true" />
        )}
        {paused ? LIVE_POLL_COPY.resumePages : LIVE_POLL_COPY.pausePages}
      </button>
      <button type="button" onClick={hold(onNext)} aria-disabled={busy} className={BUTTON}>
        <StageHitArea />
        {LIVE_POLL_COPY.nextPage}
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
      </button>
    </QuietBar>
  );
}
